import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  PublicConversationResponse,
  PublicQuestionsResponse,
  SuggestionChips,
} from '@arcanum-hq/shared-types';
import { api, ApiError, type ModelViewPayload } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';

/**
 * `agent-ux-optimization` Phase 6 Session 6 (2026-05-08).
 *
 * Web-side hooks for the public deal-link surface at `/deal/:shareId`.
 *
 *   - `usePublicDeal(shareId)` fetches `/api/shared/:shareId/deal`. The
 *     endpoint is unauthenticated; the request omits any `Authorization`
 *     header by going through the same `api.get` wrapper as
 *     authenticated calls — the wrapper attaches the auth token only when
 *     the user is signed in, otherwise sends a bare GET. Anonymous
 *     viewers get a public-safe projection (no `user_id`, no auth-only
 *     fields).
 *
 *   - `useRecordQuestion(shareId)` POSTs to
 *     `/api/shared/:shareId/questions`. Returns `{ count, gateActive }`;
 *     the cookie `viewer_session_id` round-trips automatically (browser
 *     `fetch` includes credentials when `credentials: 'include'` is set
 *     by the API wrapper). The mutation invalidates the question-count
 *     query key so any dependent UI re-renders on new state.
 */

export interface PublicDeal {
  shareId: string;
  conversationId: string;
  title: string;
  modelId: string;
  modelVersion: string;
  signalVariant: 'operating-asset' | 'new-development' | null;
  /**
   * `external-sharing` Phase 1 extended the wire shape with `ownerName`,
   * `viewerKind`, `message`, and `createdAt`. The web type catches up to
   * the server's `publicDealResponseSchema` projection (Phase 4 Session 4.2,
   * 2026-05-09 — needed by `<SharedDealBanner>` for the trust line).
   * Optional + nullable so legacy unauth public-deal callers (Phase 6's
   * `/deal/:shareId` route) can ignore the new fields without breaking.
   */
  ownerName?: string;
  /**
   * Server-computed discriminator. Canonical 4-value enum (matches
   * `viewerKindSchema` in shared-types AND `NarrativeBarShareMode`):
   *   - `'external-anon'`: unauth viewer
   *   - `'external-authed'`: signed-in non-recipient (forwardee, etc.)
   *   - `'internal-recipient'`: signed-in viewer whose `auth.uid()` matches
   *     `shared_links.recipient_user_id`
   *   - `'owner'`: signed-in viewer is the share owner (only reachable via
   *     `?preview=true` bypass; Phase 6's owner-redirect short-circuits the
   *     normal flow)
   */
  viewerKind?: 'external-anon' | 'external-authed' | 'internal-recipient' | 'owner';
  message?: string | null;
  createdAt?: string;
  /**
   * Geocoded coordinates for the deal's `PropertyAddress`, populated by
   * the API's `geocodeAndCache` post-write hook (`external-sharing` Phase 6
   * Session 6.4, 2026-05-09) and projected onto the response by
   * `SharingService.getPublicDeal` (live agent_state read, NOT
   * frozen_state — geocoded coords may post-date the share's snapshot).
   * Consumed by `<PropertyMap>` via `<SignalOperatingAsset>` /
   * `<SignalNewDevelopment>`'s `propertyLat`/`propertyLng` props.
   * Optional + nullable: legacy server responses without the projection
   * surface as `undefined`, which the consumer treats identically to `null`.
   */
  aerialLatLng?: { lat: number; lng: number } | null;
  /**
   * `deal-signal-overhaul` Phase 5 Session 2 (2026-05-12). 3 deal-specific
   * suggestion chips generated at share creation by Phase 3's
   * `generateSuggestionChips` (single Haiku call against the schema +
   * liveMetrics) and persisted to `shared_links.suggestion_chips jsonb`.
   * Projected onto `publicDealResponseSchema.suggestionChips` (sharing.ts
   * line 315). `<PublicDealChatThread>`'s empty-state preamble (Phase 5
   * Session 3) renders them as click-to-send chips. NULL when chip
   * generation failed (fail-OPEN — share creation proceeded with
   * `suggestion_chips: null`); consumers fall back to a chip-less empty
   * state.
   */
  suggestionChips?: SuggestionChips | null;
}

interface PublicDealResponse {
  success: boolean;
  data: PublicDeal;
}

export interface RecordQuestionResult {
  count: number;
  gateActive: boolean;
}

interface RecordQuestionResponse {
  success: boolean;
  data: RecordQuestionResult;
}

const publicDealQueryKey = (shareId: string) =>
  ['public-deal', shareId] as const;

/**
 * Fetch the public-safe deal projection for an unauthenticated viewer.
 * The hook returns `data: undefined` while loading; consumers should
 * gate body rendering on `data != null` (matches the existing
 * `useConversation` contract). 404 surfaces as `error` (consumers render
 * a "deal not found" empty state).
 *
 * **Auth-state gating** (`external-sharing` Phase 5 Pass 3 walkthrough fix
 * 2026-05-09): the query is gated on `!authLoading` to avoid firing the
 * GET before `useAuth()` has resolved the Supabase session. Without the
 * gate, signed-in viewers initially fetch without a Bearer token —
 * server's `optionalAuth` reads no auth → returns `viewerKind:
 * 'external-anon'` even for the share's recipient. TanStack caches the
 * response (60s staleTime) so the wrong viewerKind sticks for the first
 * minute of the session, breaking `<NarrativeBar shareMode>` resolution
 * + the `?preview=true` owner-bypass + every recipient-mode
 * affordance. The gate ensures the first fetch carries the resolved
 * Bearer (or genuinely no Bearer for unauth viewers — `authLoading`
 * settles to `false` either way once the initial Supabase getSession
 * round-trip completes).
 */
export function usePublicDeal(shareId: string | null) {
  const { loading: authLoading, user } = useAuth();
  return useQuery({
    // Query key carries the user's auth state so transitioning from
    // unauth → authed (or vice versa) re-fetches with the new Bearer
    // header instead of returning the cached anon response.
    queryKey: shareId
      ? [...publicDealQueryKey(shareId), user?.id ?? null]
      : ['public-deal', 'none'],
    queryFn: async (): Promise<PublicDeal> => {
      if (!shareId) throw new Error('shareId required');
      const res = await api.get<PublicDealResponse>(`/api/shared/${shareId}/deal`);
      return res.data;
    },
    enabled: !!shareId && !authLoading,
    // Public deals don't change often from the viewer's perspective; the
    // 1-minute stale time keeps re-renders cheap on tab switches.
    staleTime: 60_000,
    retry: (failureCount, error) => {
      // Don't retry 404 — the deal genuinely doesn't exist.
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 1;
    },
  });
}

/**
 * `external-sharing` Phase 4 Session 4.2 (2026-05-09).
 *
 * Read-only share Builder mode (`/builder?share=<id>`) consumes the same
 * `ModelViewPayload` shape `useModelView(conversationId)` returns — but
 * sourced from `GET /api/shared/:shareId/model-view`. The endpoint serves
 * the share's `frozen_state.inputValues` + the model's layout artifact;
 * service code path is `SharingService.getSharedModelView` (Phase 2 Session
 * 2.1d). Discriminated `layoutMissing` branch surfaces just like the auth
 * flow — consumers narrow on `data.layoutMissing` to render
 * `<LayoutUnavailablePanel>` vs the full `<ModelLayout>`.
 *
 * Cache strategy: 60s staleTime (matches `usePublicDeal`; the snapshot is
 * immutable, so longer is acceptable but 60s keeps invalidations cheap if
 * we add manual refresh later). 404 → no retry; the share is genuinely
 * unavailable (revoked or invalid id). 5xx → 1 retry then surface error.
 */
const sharedModelViewQueryKey = (shareId: string) =>
  ['shared-model-view', shareId] as const;

export function useSharedModelView(shareId: string | null) {
  return useQuery({
    queryKey: shareId
      ? sharedModelViewQueryKey(shareId)
      : ['shared-model-view', 'none'],
    queryFn: async (): Promise<ModelViewPayload> => {
      if (!shareId) throw new Error('shareId required');
      // Endpoint is public — `lib/api.ts` request wrapper attaches the auth
      // header when a session exists, but the server side doesn't gate on
      // it (recipient might be authenticated or anonymous; either way,
      // service-role reads the row).
      const envelope = await api.get<{ success: boolean; data: ModelViewPayload }>(
        `/api/shared/${shareId}/model-view`,
      );
      return envelope.data;
    },
    enabled: !!shareId,
    staleTime: 60_000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 1;
    },
  });
}

/**
 * Record a viewer question against the soft 3-question gate. The cookie
 * `viewer_session_id` is set by the server on first call and re-used on
 * subsequent calls automatically (browser cookie jar). The mutation
 * intentionally does NOT invalidate the public-deal query: the deal
 * payload doesn't carry the question count, so an invalidate would
 * just re-fetch the same projection (gap-analysis 2026-05-08).
 *
 * **Phase 5 deprecation note**: `<PublicDealNarrativeBar>` no longer
 * calls this hook on submit — the chat-stream POST
 * (`useSharedStreaming.sendMessage`) atomically increments the
 * per-share counter server-side. `useRecordQuestion` stays exported
 * for back-compat (no current consumer outside the legacy bar wiring
 * in `routes/deal.tsx` which Phase 5 Session 4 rewrites) but is on
 * the deprecation path.
 */
export function useRecordQuestion(shareId: string) {
  return useMutation({
    mutationFn: async (): Promise<RecordQuestionResult> => {
      const res = await api.post<RecordQuestionResponse>(
        `/api/shared/${shareId}/questions`,
        {},
      );
      return res.data;
    },
  });
}

/**
 * `deal-signal-overhaul` Phase 5 Session 2 (2026-05-12).
 *
 * Fetch the per-share conversation thread (`turns[]` + gate state) from
 * `GET /api/shared/:shareId/conversation`. Phase 3's projection
 * intentionally omits `viewer_session_id` from the wire — every viewer
 * of the same link sees the same anonymized thread.
 *
 * Cache strategy mirrors `usePublicDeal`: 60s staleTime, 404 → no retry,
 * 5xx → 1 retry. The `<PublicDealChatThread>` mount-hydration path reads
 * `data.turns`; the in-flight stream's `done` event invalidates this key
 * to swap the optimistic stub for the persisted turn (Session 3).
 */
export function usePublicDealConversation(shareId: string | null) {
  return useQuery({
    queryKey: shareId
      ? (['public-deal-conversation', shareId] as const)
      : (['public-deal-conversation', 'none'] as const),
    queryFn: async (): Promise<PublicConversationResponse> => {
      if (!shareId) throw new Error('shareId required');
      const envelope = await api.get<{ success: boolean; data: PublicConversationResponse }>(
        `/api/shared/${shareId}/conversation`,
      );
      return envelope.data;
    },
    enabled: !!shareId,
    staleTime: 60_000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 1;
    },
  });
}

/**
 * `deal-signal-overhaul` Phase 5 Session 2 (2026-05-12).
 *
 * Fetch the gate-state projection (`{ count, gateActive }`) from
 * `GET /api/shared/:shareId/questions`. Coexists with the existing
 * `POST /api/shared/:shareId/questions` (same URL, different method —
 * Express dispatches cleanly). Cheap projection (single SELECT against
 * `shared_links.public_question_count`).
 *
 * Same cache strategy as siblings (60s staleTime, no-retry-on-404).
 * Currently unused inline by the chat surface — `usePublicDealConversation`
 * already surfaces `gateActive` + `questionCount`. Exported for any
 * future consumer that needs the lightweight projection in isolation
 * (e.g. an analytics hook, a counter badge outside the thread frame).
 */
export function usePublicDealQuestions(shareId: string | null) {
  return useQuery({
    queryKey: shareId
      ? (['public-deal-questions', shareId] as const)
      : (['public-deal-questions', 'none'] as const),
    queryFn: async (): Promise<PublicQuestionsResponse> => {
      if (!shareId) throw new Error('shareId required');
      const envelope = await api.get<{ success: boolean; data: PublicQuestionsResponse }>(
        `/api/shared/${shareId}/questions`,
      );
      return envelope.data;
    },
    enabled: !!shareId,
    staleTime: 60_000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 1;
    },
  });
}
